import { Trans } from '@lingui/react/macro';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@repo/videa-components/src/index';

import type { ReferralRecord } from '../referralPageTypes';

interface SubmitReferralModalProps {
  isOpen: boolean;
  referral: ReferralRecord;
  onClose: () => void;
  onConfirm: () => void;
}

export const SubmitReferralModal = ({
  isOpen,
  referral,
  onClose,
  onConfirm,
}: SubmitReferralModalProps) => {
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="vai-max-w-[600px] vai-p-6">
        <DialogHeader className="vai-pb-2">
          <DialogTitle>
            <Trans>Send Referral</Trans>
          </DialogTitle>
        </DialogHeader>

        <div className="vai-space-y-6">
          {/* Patient Information Display */}
          <div className="vai-space-y-3">
            <div className="vai-text-sm vai-font-medium vai-text-gray-700">
              <Trans>Patient Information</Trans>
            </div>
            <div className="vai-rounded-lg vai-border-2 vai-bg-blue-50 vai-border-blue-200 vai-p-4">
              <div className="vai-text-sm vai-font-semibold vai-mb-2 vai-text-blue-900">
                <Trans>Selected Patient</Trans>
              </div>
              <div className="vai-text-sm vai-text-blue-800">
                <div className="vai-font-medium vai-text-base">
                  {referral.patientFirstName} {referral.patientLastName}
                </div>
                {referral.patientPhone && (
                  <div className="vai-text-xs vai-mt-1 vai-text-blue-600">
                    <Trans>Phone:</Trans> {referral.patientPhone}
                  </div>
                )}
                {referral.patientEmail && (
                  <div className="vai-text-xs vai-mt-1 vai-text-blue-600">
                    <Trans>Email:</Trans> {referral.patientEmail}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Specialty Information */}
          {referral.specialtyName && (
            <div className="vai-space-y-3">
              <div className="vai-text-sm vai-font-medium vai-text-gray-700">
                <Trans>Referral Details</Trans>
              </div>
              <div className="vai-rounded-lg vai-border vai-border-gray-200 vai-bg-gray-50 vai-p-4">
                <div className="vai-text-sm">
                  <div className="vai-mb-2">
                    <span className="vai-font-semibold vai-text-gray-700">
                      <Trans>Specialty:</Trans>
                    </span>{' '}
                    <span className="vai-text-gray-900">
                      {referral.specialtyName}
                    </span>
                  </div>
                  {referral.destinationPracticeName && (
                    <div>
                      <span className="vai-font-semibold vai-text-gray-700">
                        <Trans>Practice:</Trans>
                      </span>{' '}
                      <span className="vai-text-gray-900">
                        {referral.destinationPracticeName}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Helper Text */}
          <div className="vai-text-sm vai-text-gray-600 vai-bg-gray-50 vai-p-3 vai-rounded-lg">
            <Trans>
              Click &ldquo;Continue&rdquo; to complete and submit this referral.
            </Trans>
          </div>

          {/* Action Buttons */}
          <div className="vai-flex vai-justify-end vai-gap-3 vai-pt-2 vai-border-t vai-border-gray-200">
            <Button
              onClick={onClose}
              variant="outline"
              className="vai-min-w-[100px]"
            >
              <Trans>Cancel</Trans>
            </Button>
            <Button
              onClick={onConfirm}
              className="vai-min-w-[120px] vai-bg-blue-600 hover:vai-bg-blue-700"
            >
              <Trans>Continue</Trans>
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
